import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { List, Pin } from 'lucide-react';
import { formatChatMessagePreview } from '@/components/chat/formatChatMessagePreview';
import type { ChatPin } from '@/hooks/useChatPins';

type Props = {
  pins: ChatPin[];
  /** Scroll the thread to the newest pin. */
  onJumpTo: (pin: ChatPin) => void;
  /** Open the full list, which is also where unpinning lives. */
  onOpenList: () => void;
};

/**
 * The strip under the chat header showing what is pinned.
 *
 * Sits between `chatHeader` and `chatBody` in both layout branches. `flexShrink={0}` is
 * load-bearing: the parent is a column flex whose message list is `flex={1} minH={0}`, so
 * without it the banner is the thing that gets squeezed away.
 *
 * Shows the newest pin and a count rather than stacking every pin — the header region is
 * 73px and a phone is narrow. The rest are one tap away in the sheet.
 *
 * TWO TARGETS, NOT ONE. The body jumps straight to the newest pin, which is what someone
 * tapping a banner means nine times in ten. The list button beside it is not a
 * convenience: the sheet is the only place unpinning is offered, so folding it into the
 * body would leave an admin able to reach a pin but never remove it. They are siblings
 * rather than nested because a button inside a button is invalid HTML and behaves
 * accordingly.
 */
export function PinnedMessageBanner({ pins, onJumpTo, onOpenList }: Props) {
  if (pins.length === 0) return null;

  const newest = pins[0];
  // formatChatMessagePreview gives "Photo" / "PDF" for an attachment-only message, so an
  // image pin reads as something rather than as an empty line.
  const preview = formatChatMessagePreview(newest.message);

  return (
    <Flex
      align="center"
      gap={1}
      flexShrink={0}
      pl={{ base: 3, md: 6 }}
      pr={{ base: 1, md: 4 }}
      bg="volyx.lightGray"
      borderBottomWidth={1}
      borderColor="volyx.borderLight"
    >
      <chakra.button
        type="button"
        // A pin whose message has gone still shows -- an admin needs to see it to clear
        // it -- but there is nowhere to jump to.
        disabled={!newest.message}
        onClick={() => {
          if (newest.message) onJumpTo(newest);
        }}
        display="flex"
        alignItems="center"
        gap={3}
        flex={1}
        minW={0}
        textAlign="start"
        py={2.5}
        borderRadius="md"
        cursor={newest.message ? 'pointer' : 'default'}
        _hover={newest.message ? { bg: 'volyx.primaryLight' } : undefined}
        aria-label={newest.message ? 'Go to pinned message' : 'Pinned message'}
      >
        <Box color="volyx.action" flexShrink={0}>
          <Pin size={16} />
        </Box>
        <Flex direction="column" minW={0} flex={1}>
          <Text fontSize="xs" fontWeight="semibold" color="volyx.action">
            {pins.length > 1 ? `Pinned · 1 of ${pins.length}` : 'Pinned'}
          </Text>
          <Text fontSize="sm" color="volyx.primary" lineClamp={1}>
            {newest.message ? preview : 'This message is no longer available'}
          </Text>
        </Flex>
      </chakra.button>

      <chakra.button
        type="button"
        onClick={onOpenList}
        aria-label={
          pins.length > 1 ? `Show all ${pins.length} pinned messages` : 'Show pinned messages'
        }
        flexShrink={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        // 44px is Apple's minimum touch target, and this sits right at the edge of the
        // screen next to a much larger tap area -- a 32px one was easy to miss and easy
        // to hit the jump with instead.
        minW="44px"
        minH="44px"
        borderRadius="md"
        color="volyx.textSecondary"
        cursor="pointer"
        _hover={{ bg: 'volyx.primaryLight', color: 'volyx.action' }}
      >
        <List size={20} />
      </chakra.button>
    </Flex>
  );
}
